/* ============================================================
   SAM-1237 — the Location block of a business (canvas v306): New business and Settings › Profile share it.
   Three types: «Cap Cana» (the tenant's name; estate group → building when the group has buildings → unit or
   shop), External (address, city, zip code, country) and Virtual (no location). The fields follow the type.
   New file under the BaseUI freeze (SAM-175): Tailwind + tokens.
   ============================================================ */
import React from 'react';
import { useNationalities } from '../../../hooks/useNationalities';
import { locationIssues, locationSummary, type LocationForm, type LocationIssue, type LocationType } from './locationModel';
import { useGroupBuildings, useGroupUnits, useLocationGroups, useUnitName } from './useLocationData';

export const LOCATION_ERROR_ID = 'bl-error';

const inputCls = 'h-10 w-full px-3 rounded-[8px] border border-solid border-[var(--line)] bg-white text-[14px] text-[var(--ink-900)] disabled:bg-[#f9f9f9] disabled:text-[var(--ink-500)]';
const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-[var(--ink-900)]';

const TYPES: Array<{ value: LocationType; title: (tenant: string) => string; hint: string }> = [
  { value: 'internal', title: (tenant) => tenant, hint: 'Estate group, building, unit' },
  { value: 'external', title: () => 'External', hint: 'Outside the community' },
  { value: 'virtual', title: () => 'Virtual', hint: 'Online / ecommerce' },
];

const Field: React.FC<{ id: string; label: string; error?: string; children: React.ReactNode }> = ({ id, label, error, children }) => (
  <div className={labelCls}>
    <label htmlFor={id}>{label}</label>
    {children}
    {error && <div role="alert" className="text-[12px] font-normal text-[var(--danger,#b42318)]">{error}</div>}
  </div>
);

/** Searchable unit or shop: the units of a group can be thousands, so it asks the server as you type. */
const UnitPicker: React.FC<{
  groupId: number; buildingId: number | null; value: number | null; onChange: (id: number | null) => void;
}> = ({ groupId, buildingId, value, onChange }) => {
  const [text, setText] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const { data: selectedName } = useUnitName(value);
  const { data: options = [], isFetching } = useGroupUnits(groupId, buildingId, text, open);
  return (
    <div className="relative">
      <input
        id="bl-unit"
        role="combobox"
        aria-expanded={open}
        aria-controls="bl-unit-list"
        aria-label="Unit or shop"
        autoComplete="off"
        className={inputCls}
        placeholder="None"
        value={open ? text : (value != null ? (selectedName ?? '') : '')}
        onFocus={() => { setText(''); setOpen(true); }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <ul id="bl-unit-list" role="listbox" className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-[8px] border border-solid border-[var(--line)] bg-white p-1 shadow-lg list-none m-0">
          <li role="option" aria-selected={value == null} className="px-3 py-2 text-[14px] text-[var(--ink-500)] cursor-pointer rounded hover:bg-[#f3f3f3]" onMouseDown={() => { onChange(null); setOpen(false); }}>None</li>
          {options.map((o) => (
            <li key={o.id} role="option" aria-selected={o.id === value} className="px-3 py-2 text-[14px] cursor-pointer rounded hover:bg-[#f3f3f3]" onMouseDown={() => { onChange(o.id); setOpen(false); }}>{o.name}</li>
          ))}
          {!isFetching && options.length === 0 && <li className="px-3 py-2 text-[13px] text-[var(--ink-500)]">No match</li>}
        </ul>
      )}
    </div>
  );
};

export const BusinessLocationFields: React.FC<{
  value: LocationForm;
  onChange: (next: LocationForm) => void;
  tenantName: string;
  /** Show what is missing (after the first try to save). */
  showIssues?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, tenantName, showIssues, disabled }) => {
  const { groups } = useLocationGroups();
  const { data: buildings = [] } = useGroupBuildings(value.type === 'internal' ? value.groupId : null);
  const { data: countries = [] } = useNationalities();
  const issues = showIssues ? locationIssues(value) : [];
  const has = (i: LocationIssue) => issues.includes(i);
  const set = (patch: Partial<LocationForm>) => onChange({ ...value, ...patch });
  const hasBuildings = buildings.length > 0;
  const showUnit = value.groupId != null && (!hasBuildings || value.buildingId != null);
  const tenant = tenantName || 'Cap Cana';

  return (
    <div className="flex flex-col gap-3.5">
      <div role="radiogroup" aria-label="Location type" className="flex gap-2.5 flex-wrap sm:flex-nowrap">
        {TYPES.map((t) => {
          const on = value.type === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => !on && onChange({ ...value, type: t.value, groupId: null, buildingId: null, unitId: null })}
              className={`flex-1 min-w-[140px] box-border text-left rounded-[10px] px-3.5 py-3 flex gap-2.5 items-start cursor-pointer border border-solid ${on ? 'border-[var(--tenant-primary,#005488)] bg-[#ebf1f5]' : 'border-[var(--line)] bg-white'}`}
            >
              <span aria-hidden className={`mt-0.5 w-4 h-4 rounded-full border-2 border-solid box-border flex items-center justify-center shrink-0 ${on ? 'border-[var(--tenant-primary,#005488)]' : 'border-[#b3b3b3]'}`}>
                {on && <span className="w-2 h-2 rounded-full bg-[var(--tenant-primary,#005488)]" />}
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-[14px] font-semibold text-[var(--ink-900)]">{t.title(tenant)}</span>
                <span className="text-[12px] text-[var(--ink-500)]">{t.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      {showIssues && value.type === '' && <div role="alert" className="text-[12px] text-[var(--danger,#b42318)]">Choose where the business is</div>}

      {value.type === 'internal' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <Field id="bl-group" label="Estate group" error={has('group') ? 'Pick an estate group' : undefined}>
              <select
                id="bl-group"
                className={inputCls}
                disabled={disabled}
                value={value.groupId ?? ''}
                onChange={(e) => set({ groupId: e.target.value === '' ? null : Number(e.target.value), buildingId: null, unitId: null })}
              >
                <option value="">Select a group…</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
            {value.groupId != null && hasBuildings && (
              <Field id="bl-building" label="Building">
                <select
                  id="bl-building"
                  className={inputCls}
                  disabled={disabled}
                  value={value.buildingId ?? ''}
                  onChange={(e) => set({ buildingId: e.target.value === '' ? null : Number(e.target.value), unitId: null })}
                >
                  <option value="">No building</option>
                  {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
            )}
            {showUnit && (
              <Field id="bl-unit" label="Unit or shop (optional)">
                <UnitPicker
                  groupId={value.groupId as number}
                  buildingId={hasBuildings ? value.buildingId : null}
                  value={value.unitId}
                  onChange={(unitId) => set({ unitId })}
                />
              </Field>
            )}
          </div>
          <p className="m-0 text-[12px] text-[var(--ink-500)]">Pick a group first. Building shows only if the group has buildings; unit follows. External and Virtual have no group.</p>
        </>
      )}

      {value.type === 'external' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div className="sm:col-span-2">
            <Field id="bl-address" label="Address" error={has('address') ? 'Enter the address' : undefined}>
              <input id="bl-address" className={inputCls} disabled={disabled} maxLength={255} value={value.address} onChange={(e) => set({ address: e.target.value })} />
            </Field>
          </div>
          <Field id="bl-city" label="City" error={has('city') ? 'Enter the city' : undefined}>
            <input id="bl-city" className={inputCls} disabled={disabled} maxLength={120} value={value.city} onChange={(e) => set({ city: e.target.value })} />
          </Field>
          <Field id="bl-zip" label="Zip code">
            <input id="bl-zip" className={inputCls} disabled={disabled} maxLength={20} value={value.zip} onChange={(e) => set({ zip: e.target.value })} />
          </Field>
          <Field id="bl-country" label="Country" error={has('country') ? 'Pick the country' : undefined}>
            <select id="bl-country" className={inputCls} disabled={disabled} value={value.country.toUpperCase()} onChange={(e) => set({ country: e.target.value })}>
              <option value="">Select a country…</option>
              {countries.map((c) => <option key={c.country_code} value={c.country_code.toUpperCase()}>{c.country}</option>)}
            </select>
          </Field>
        </div>
      )}

      {value.type === 'virtual' && (
        <p className="m-0 text-[14px] text-[var(--ink-700)]">Guests see no location in the app profile.</p>
      )}
    </div>
  );
};

/** Settings › Profile, read mode: the stored location as label and value rows. */
export const BusinessLocationRead: React.FC<{ value: LocationForm; tenantName: string }> = ({ value, tenantName }) => {
  const { groups } = useLocationGroups();
  const { data: buildings = [] } = useGroupBuildings(value.type === 'internal' ? value.groupId : null);
  const { data: unitName } = useUnitName(value.type === 'internal' ? value.unitId : null);
  const { data: countries = [] } = useNationalities();
  const row = (label: string, text?: string) => text ? (
    <div className="grid grid-cols-[150px_minmax(0,1fr)] items-center gap-3 text-[14px] min-h-[38px]">
      <span className="text-[var(--ink-500)]">{label}</span><span>{text}</span>
    </div>
  ) : null;
  if (value.type === '') return <p className="m-0 text-[14px] text-[var(--ink-500)]">Not set yet.</p>;
  if (value.type === 'virtual') return <>{row('Type', 'Virtual')}<p className="m-0 text-[14px] text-[var(--ink-500)]">Guests see no location in the app profile.</p></>;
  if (value.type === 'external') {
    return (<>
      {row('Type', 'External')}
      {row('Address', value.address)}
      {row('City', value.city)}
      {row('Zip code', value.zip)}
      {row('Country', countries.find((c) => c.country_code.toUpperCase() === value.country.toUpperCase())?.country ?? value.country)}
    </>);
  }
  return (<>
    {row('Type', tenantName || 'Cap Cana')}
    {row('Estate group', groups.find((g) => g.id === value.groupId)?.name)}
    {row('Building', buildings.find((b) => b.id === value.buildingId)?.name)}
    {row('Unit or shop', unitName)}
  </>);
};

export { locationSummary };
